import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import GlobalStyles from "@mui/material/GlobalStyles";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { ArrowSquareOut as ArrowSquareOutIcon } from "@phosphor-icons/react/ArrowSquareOut";
import { CaretDown as CaretDownIcon } from "@phosphor-icons/react/CaretDown";
import { CaretRight as CaretRightIcon } from "@phosphor-icons/react/CaretRight";
import { ChartLine as ChartLineIcon } from "@phosphor-icons/react/ChartLine";
import { CompassTool as CompassToolIcon } from "@phosphor-icons/react/CompassTool";
import { CreditCard as CreditCardIcon } from "@phosphor-icons/react/CreditCard";
import { Database } from "@phosphor-icons/react/Database";
import { Icon } from "@phosphor-icons/react/dist/lib/types";
import { Faders as FadersIcon } from "@phosphor-icons/react/Faders";
import { FadersHorizontal as FadersHorizontalIcon } from "@phosphor-icons/react/FadersHorizontal";
import { Globe as GlobeIcon } from "@phosphor-icons/react/Globe";
import { IdentificationCard as IdentificationCardIcon } from "@phosphor-icons/react/IdentificationCard";
import { Info as InfoIcon } from "@phosphor-icons/react/Info";
import { MapTrifold as MapTrifoldIcon } from "@phosphor-icons/react/MapTrifold";
import { Palette as PaletteIcon } from "@phosphor-icons/react/Palette";
import { Password as PasswordIcon } from "@phosphor-icons/react/Password";
import { Question as QuestionIcon } from "@phosphor-icons/react/Question";
import { Receipt as ReceiptIcon } from "@phosphor-icons/react/Receipt";
import { SealCheck as SealCheckIcon } from "@phosphor-icons/react/SealCheck";
import { SignOut as SignOutIcon } from "@phosphor-icons/react/SignOut";
import { Stack as StackIcon } from "@phosphor-icons/react/Stack";
import { StackSimple as StackSimpleIcon } from "@phosphor-icons/react/StackSimple";
import { Swap as SwapIcon } from "@phosphor-icons/react/Swap";
import { Translate as TranslateIcon } from "@phosphor-icons/react/Translate";
import { UserCircle as UserCircleIcon } from "@phosphor-icons/react/UserCircle";
import { UserPlus as UserPlusIcon } from "@phosphor-icons/react/UserPlus";
import { UsersFour as UsersFourIcon } from "@phosphor-icons/react/UsersFour";
import { UsersThree as UsersThreeIcon } from "@phosphor-icons/react/UsersThree";
import { paths, routes } from "config/paths";
import RouterLink from "next/link";
import {
	Children,
	cloneElement,
	createContext,
	Fragment,
	KeyboardEvent,
	ReactNode,
	useContext,
	useState,
} from "react";
import { useSettings } from "~ui-components/hooks/use-settings";
import { isNavItemActive } from "~ui-components/libs/is-nav-item-active";
import { Account, User } from "~ui-components/types/__generated/gql/graphql";
import { NavItemConfig } from "~ui-components/types/nav";
import { useWebRouter } from "~utils/next-router/use-web-router";
import { getRecurlyHostedBillingInfoUrl } from "~utils/recurly/get-recurly-hosted-billing-info-url";
import { MainNav, useNav } from "./main-nav";
import { SideNav } from "./side-nav";

const NavVariantContext = createContext<"side" | "mobile">("side");
const NavVariant = NavVariantContext.Provider;

export interface VerticalLayoutProps {
	children: ReactNode;
	isAuthenticated: boolean;
	isTrialExpired?: boolean;
	isAccountClosed?: boolean;
	onSignOut: () => void;
	isAccountOwner: boolean;
	showMapNavGroup: boolean;
	showDatasetNavGroup: boolean;
	hasMapEditAccess: boolean;
	hasDatasetEditAccess: boolean;
	user?: User;
	account?: Account;
}

export function VerticalLayout(props: VerticalLayoutProps) {
	const {
		children,
		isAuthenticated,
		isTrialExpired,
		isAccountClosed,
		onSignOut,
	} = props;
	const settings = useSettings();

	return (
		<Fragment>
			<GlobalStyles
				styles={{
					body: {
						"--MainNav-height": "56px",
						"--MainNav-zIndex": 1000,
						"--SideNav-width": "280px",
						"--SideNav-zIndex": 1100,
						"--MobileNav-width": "320px",
						"--MobileNav-zIndex": 1100,
						"--MapTool-width": "400px",
					},
				}}
			/>
			<Box
				sx={{
					bgcolor: "var(--mui-palette-background-default)",
					display: "flex",
					flexDirection: "column",
					position: "relative",
					minHeight: "100%",
				}}>
				{isAuthenticated && !isTrialExpired && !isAccountClosed && (
					<SideNav color={settings.navColor}>
						<NavGroups
							variant="side"
							{...props}
						/>
					</SideNav>
				)}
				<Box
					sx={{
						display: "flex",
						flex: "1 1 auto",
						flexDirection: "column",
						pl:
							isAuthenticated && !isTrialExpired && !isAccountClosed
								? { lg: "var(--SideNav-width)" }
								: "unset",
					}}>
					{isAuthenticated && !isTrialExpired && !isAccountClosed && (
						<MainNav>
							<NavGroups
								variant="mobile"
								{...props}
							/>
						</MainNav>
					)}
					<Box
						component="main"
						sx={{
							"--Content-margin": "0 auto",
							"--Content-maxWidth": "var(--maxWidth-xl)",
							"--Content-paddingX": "24px",
							"--Content-paddingY": { xs: "24px", lg: "64px" },
							"--Content-padding":
								"var(--Content-paddingY) var(--Content-paddingX)",
							"--Content-width": "100%",
							display: "flex",
							flex: "1 1 auto",
							flexDirection: "column",
						}}>
						{children}

						{(!!isTrialExpired || !!isAccountClosed) && (
							<Box
								sx={{
									maxWidth: "var(--Content-maxWidth)",
									m: "var(--Content-margin)",
									px: "var(--Content-paddingX)",
									pb: "var(--Content-paddingY)",
									width: "var(--Content-width)",
								}}>
								{!!isTrialExpired && (
									<Alert
										severity="info"
										action={
											<Button
												onClick={onSignOut}
												color="inherit"
												size="small">
												Sign out
											</Button>
										}>
										Your account will be deleted 21 days after your trial ends
										unless you upgrade to a paid subscription.
									</Alert>
								)}
								{!!isAccountClosed && (
									<Alert
										severity="info"
										action={
											<Button
												onClick={onSignOut}
												color="inherit"
												size="small">
												Sign out
											</Button>
										}>
										Your account will not be accessible until you upgrade to a
										paid subscription.
									</Alert>
								)}
							</Box>
						)}
					</Box>
				</Box>
			</Box>
		</Fragment>
	);
}

function NavGroups(
	props: VerticalLayoutProps & {
		variant: "side" | "mobile";
	},
) {
	const { _query, pathname } = useWebRouter();

	const {
		variant,
		onSignOut,
		isAccountOwner,
		showMapNavGroup,
		showDatasetNavGroup,
		user,
		account,
	} = props;

	return (
		<NavVariant value={variant}>
			<Stack
				component="ul"
				spacing={2}
				sx={{ listStyle: "none", m: 0, p: 0 }}>
				<NavGroup title="Navigation">
					<NavItems>
						<NavItem
							title="Map portal"
							icon={MapTrifoldIcon}
							href={paths.accountName.maps.root(_query.accountName)}
							matcher={{
								href: routes.accountName.maps.root,
								type: "startsWith",
							}}
						/>
						<NavItem
							title="Data portal"
							icon={Database}
							href={paths.accountName.data.root(_query.accountName)}
							matcher={{
								href: routes.accountName.data.root,
								type: "startsWith",
							}}
						/>
					</NavItems>
				</NavGroup>

				{showMapNavGroup && <MapNavGroup {...props} />}

				{showDatasetNavGroup && <DatasetNavGroup {...props} />}

				{isAccountOwner && (
					<NavGroup title="Global settings">
						<NavItems>
							<NavItem
								title="Team"
								icon={UsersFourIcon}
								forceOpen={[
									routes.accountName.users.root,
									routes.accountName.groups.root,
								].some((path) => pathname.startsWith(path))}>
								<NavItem
									title="Users"
									icon={UserCircleIcon}
									href={paths.accountName.users.root(_query.accountName)}
									matcher={{
										href: routes.accountName.users.root,
										type: "startsWith",
									}}
								/>
								<NavItem
									title="Access groups"
									icon={UsersThreeIcon}
									href={paths.accountName.groups.root(_query.accountName)}
									matcher={{
										href: routes.accountName.groups.root,
										type: "startsWith",
									}}
								/>
							</NavItem>

							<NavItem
								title="Customisation"
								icon={PaletteIcon}
								forceOpen={[
									routes.accountName.branding.root,
									routes.accountName.branding.localisation,
									routes.accountName.branding.domainAndWhiteLabel,
								].includes(pathname)}>
								<NavItem
									title="Brand"
									icon={SealCheckIcon}
									href={paths.accountName.branding.root(_query.accountName)}
									matcher={{
										href: routes.accountName.branding.root,
										type: "equals",
									}}
								/>
								<NavItem
									title="Localisation"
									icon={TranslateIcon}
									href={paths.accountName.branding.localisation(
										_query.accountName,
									)}
									matcher={{
										href: routes.accountName.branding.localisation,
										type: "equals",
									}}
								/>
								<NavItem
									title="Domain & whitelabel"
									icon={GlobeIcon}
									href={paths.accountName.branding.domainAndWhiteLabel(
										_query.accountName,
									)}
									matcher={{
										href: routes.accountName.branding.domainAndWhiteLabel,
										type: "equals",
									}}
								/>
							</NavItem>
						</NavItems>
					</NavGroup>
				)}

				<NavGroup title="Account">
					<NavItems>
						<NavItem
							title="Support"
							icon={QuestionIcon}
							href={paths.accountName.support(_query.accountName)}
							matcher={{
								href: routes.accountName.support.root,
								type: "startsWith",
							}}
						/>
						{isAccountOwner && (
							<NavItem
								title="Analytics"
								icon={ChartLineIcon}
							/>
						)}
						{isAccountOwner && (
							<NavItem
								title="Subscription & billing"
								icon={SealCheckIcon}
								forceOpen={[
									routes.accountName.settings.root,
									routes.accountName.settings.editSubscription,
									routes.accountName.settings.invoice,
								].includes(pathname)}>
								<NavItem
									title="Subscription summary"
									icon={InfoIcon}
									href={paths.accountName.settings.root(_query.accountName)}
									matcher={{
										href: routes.accountName.settings.root,
										type: "equals",
									}}
								/>
								<NavItem
									title="Manage subscription"
									icon={FadersIcon}
									href={paths.accountName.settings.editSubscription(
										_query.accountName,
									)}
									matcher={{
										href: routes.accountName.settings.editSubscription,
										type: "equals",
									}}
								/>
								{!!account?._recurlyAccountInfo?.hostedToken && (
									<NavItem
										title="Update billing"
										icon={CreditCardIcon}
										href={getRecurlyHostedBillingInfoUrl(
											account._recurlyAccountInfo.hostedToken,
										)}
										external
									/>
								)}
								{!!account?._recurlyAccountInfo && (
									<NavItem
										title="Invoice"
										icon={ReceiptIcon}
										href={paths.accountName.settings.invoice(
											_query.accountName,
										)}
										matcher={{
											href: routes.accountName.settings.invoice,
											type: "equals",
										}}
									/>
								)}
							</NavItem>
						)}
					</NavItems>
				</NavGroup>

				{!!user && !!account?._currentUserRole && (
					<NavGroup title="Profile">
						<NavItems>
							<NavItem
								title={user.name ?? "*USER FULL NAME*"}
								icon={UserCircleIcon}
								forceOpen={[
									routes.accountName.settings.profile,
									routes.accountName.settings.password,
								].includes(pathname)}>
								<NavItem
									title="User profile"
									icon={IdentificationCardIcon}
									href={paths.accountName.settings.profile(_query.accountName)}
									matcher={{
										href: routes.accountName.settings.profile,
										type: "equals",
									}}
								/>
								<NavItem
									title="Change password"
									icon={PasswordIcon}
									href={paths.accountName.settings.password(_query.accountName)}
									matcher={{
										href: routes.accountName.settings.password,
										type: "equals",
									}}
								/>
								<NavItem
									title="Sign out"
									icon={SignOutIcon}
									onClick={onSignOut}
								/>
							</NavItem>
						</NavItems>
					</NavGroup>
				)}
			</Stack>
		</NavVariant>
	);
}

const MapNavGroup = (props: VerticalLayoutProps) => {
	const { _query } = useWebRouter();

	const { hasMapEditAccess } = props;

	return (
		<NavGroup
			indent
			title="Map settings">
			<NavItems>
				<NavItem
					title="Map"
					icon={MapTrifoldIcon}
					href={paths.accountName.maps.mapId.mapName.root(
						_query.accountName,
						_query.mapId,
						_query.mapName,
					)}
					matcher={{
						href: routes.accountName.maps.mapId.mapName.root,
						type: "equals",
					}}
				/>
				{hasMapEditAccess && (
					<>
						<NavItem
							title="Layers"
							icon={StackIcon}
							href={paths.accountName.maps.mapId.mapName.layers.root(
								_query.accountName,
								_query.mapId,
								_query.mapName,
							)}
							matcher={{
								href: routes.accountName.maps.mapId.mapName.layers.root,
								type: "startsWith",
							}}
						/>
						<NavItem
							title="Toolbox"
							icon={CompassToolIcon}
							href={paths.accountName.maps.mapId.mapName.tools.root(
								_query.accountName,
								_query.mapId,
								_query.mapName,
							)}
							matcher={{
								href: routes.accountName.maps.mapId.mapName.tools,
								type: "startsWith",
							}}
						/>
						<NavItem
							title="Access & sharing"
							icon={UserPlusIcon}
							href={paths.accountName.maps.mapId.mapName.access(
								_query.accountName,
								_query.mapId,
								_query.mapName,
							)}
							matcher={{
								href: routes.accountName.maps.mapId.mapName.access,
								type: "startsWith",
							}}
						/>
						<NavItem
							title="Map config"
							icon={FadersHorizontalIcon}
							href={paths.accountName.maps.mapId.mapName.settings.root(
								_query.accountName,
								_query.mapId,
								_query.mapName,
							)}
							matcher={{
								href: routes.accountName.maps.mapId.mapName.settings.root,
								type: "startsWith",
							}}
						/>
					</>
				)}
			</NavItems>
		</NavGroup>
	);
};

const DatasetNavGroup = (props: VerticalLayoutProps) => {
	const { _query } = useWebRouter();

	const { hasDatasetEditAccess } = props;

	return (
		<NavGroup
			indent
			title="Data settings">
			<NavItems>
				<NavItem
					title="Dataset"
					icon={StackSimpleIcon}
					href={paths.accountName.data.datasetId.root(
						_query.accountName,
						_query.datasetId,
					)}
					matcher={{
						href: routes.accountName.data.datasetId.root,
						type: "equals",
					}}
				/>
				{hasDatasetEditAccess && (
					<>
						<NavItem
							title="Data config"
							icon={FadersHorizontalIcon}
							href={paths.accountName.data.datasetId.update(
								_query.accountName,
								_query.datasetId,
							)}
							matcher={{
								href: routes.accountName.data.datasetId.update,
								type: "equals",
							}}
						/>
						<NavItem
							title="Sync"
							icon={SwapIcon}
							href={paths.accountName.data.datasetId.sync(
								_query.accountName,
								_query.datasetId,
							)}
							matcher={{
								href: routes.accountName.data.datasetId.sync,
								type: "equals",
							}}
						/>
						<NavItem
							title="Access & sharing"
							icon={UserPlusIcon}
							href={paths.accountName.data.datasetId.access(
								_query.accountName,
								_query.datasetId,
							)}
							matcher={{
								href: routes.accountName.data.datasetId.access,
								type: "equals",
							}}
						/>
					</>
				)}
			</NavItems>
		</NavGroup>
	);
};

export function NavGroup(props) {
	const { indent, title, children } = props;
	return (
		<Stack
			component="li"
			key={title}
			spacing={1.5}>
			{title ? (
				<div>
					<Typography
						sx={{
							color: "var(--NavGroup-title-color)",
							fontSize: "0.875rem",
							fontWeight: 500,
						}}>
						{title}
					</Typography>
				</div>
			) : null}

			{indent ? (
				<Box sx={{ pl: "24px" }}>
					<Box
						sx={{
							borderLeft: "1px solid var(--NavItem-children-border)",
							pl: "12px",
						}}>
						{Children.map(children, (child) => {
							if (!child) return null;
							return cloneElement(child, { depth: 1 });
						})}
					</Box>
				</Box>
			) : (
				<div>{children}</div>
			)}
		</Stack>
	);
}

export function NavItems(props) {
	const { depth = 0, children } = props;
	return (
		<Stack
			component="ul"
			data-depth={depth}
			spacing={1}
			sx={{ listStyle: "none", m: 0, p: 0 }}>
			{Children.map(children, (child: any) => {
				if (!child) return null;
				return cloneElement(child, { depth });
			})}
		</Stack>
	);
}

interface NavItemProps extends Omit<NavItemConfig, "key" | "items" | "icon"> {
	children?: ReactNode;
	depth?: number;
	forceOpen?: boolean;
	icon?: Icon;
	onClick?: () => void;
}

export function NavItem(props: NavItemProps) {
	const {
		children,
		depth = 0,
		disabled,
		external,
		forceOpen = false,
		href,
		icon,
		label,
		matcher,
		title,
		onClick,
	} = props;

	const { onClose } = useNav();

	const variant = useContext(NavVariantContext);
	const router = useWebRouter();
	const { pathname } = router;

	const [open, setOpen] = useState<boolean>(forceOpen);

	const active = isNavItemActive({
		disabled,
		external,
		href,
		matcher,
		pathname,
	});

	const Icon = icon ?? null;
	const ExpandIcon = open ? CaretDownIcon : CaretRightIcon;
	const isBranch = children && !href;
	const showChildren = Boolean(children && open);

	return (
		<Box
			component="li"
			data-depth={depth}
			sx={{ userSelect: "none" }}>
			<Box
				{...(isBranch
					? {
							onClick: (): void => {
								setOpen(!open);
							},
							onKeyUp: (event: KeyboardEvent<HTMLDivElement>): void => {
								if (event.key === "Enter" || event.key === " ") {
									setOpen(!open);
								}
							},
							role: "button",
						}
					: {
							...(href
								? {
										component: external ? "a" : RouterLink,
										href,
										target: external ? "_blank" : undefined,
										rel: external ? "noreferrer" : undefined,
										...(variant === "mobile"
											? {
													onClick: onClose,
												}
											: {}),
									}
								: {
										role: "button",
										onKeyUp: (event: KeyboardEvent<HTMLDivElement>): void => {
											if (event.key === "Enter" || event.key === " ") {
												//
											}
										},
										onClick: () => onClick?.(),
									}),
						})}
				sx={{
					alignItems: "center",
					borderRadius: 1,
					color: "var(--NavItem-color)",
					cursor: "pointer",
					display: "flex",
					flex: "0 0 auto",
					gap: 1,
					p: "6px 16px",
					position: "relative",
					textDecoration: "none",
					whiteSpace: "nowrap",
					...(disabled && {
						bgcolor: "var(--NavItem-disabled-background)",
						color: "var(--NavItem-disabled-color)",
						cursor: "not-allowed",
					}),
					...(active && {
						bgcolor: "var(--NavItem-active-background)",
						color: "var(--NavItem-active-color)",
						...(depth > 0 && {
							"&::before": {
								bgcolor: "var(--NavItem-children-indicator)",
								borderRadius: "2px",
								content: '" "',
								height: "20px",
								left: "-14px",
								position: "absolute",
								width: "3px",
							},
						}),
					}),
					...(open && { color: "var(--NavItem-open-color)" }),
					"&:hover": {
						...(!disabled &&
							!active && {
								bgcolor: "var(--NavItem-hover-background)",
								color: "var(--NavItem-hover-color)",
							}),
					},
				}}
				tabIndex={0}>
				<Box
					sx={{
						alignItems: "center",
						display: "flex",
						justifyContent: "center",
						flex: "0 0 auto",
					}}>
					{Icon ? (
						<Icon
							fill={
								active
									? "var(--NavItem-icon-active-color)"
									: "var(--NavItem-icon-color)"
							}
							fontSize="var(--icon-fontSize-md)"
							weight={forceOpen || active ? "fill" : undefined}
						/>
					) : null}
				</Box>
				<Box sx={{ flex: "1 1 auto" }}>
					<Typography
						component="span"
						sx={{
							color: "inherit",
							fontSize: "0.875rem",
							fontWeight: 500,
							lineHeight: "28px",
						}}>
						{title}
					</Typography>
				</Box>
				{label ? (
					<Chip
						color="primary"
						label={label}
						size="small"
					/>
				) : null}
				{external ? (
					<Box sx={{ alignItems: "center", display: "flex", flex: "0 0 auto" }}>
						<ArrowSquareOutIcon
							color="var(--NavItem-icon-color)"
							fontSize="var(--icon-fontSize-sm)"
						/>
					</Box>
				) : null}
				{isBranch ? (
					<Box sx={{ alignItems: "center", display: "flex", flex: "0 0 auto" }}>
						<ExpandIcon
							color="var(--NavItem-expand-color)"
							fontSize="var(--icon-fontSize-sm)"
						/>
					</Box>
				) : null}
			</Box>
			{showChildren ? (
				<Box sx={{ pl: "24px" }}>
					<Box
						sx={{
							borderLeft: "1px solid var(--NavItem-children-border)",
							pl: "12px",
						}}>
						{Children.map(children, (child: any) => {
							if (!child) return null;
							return cloneElement(child, { depth: depth + 1 });
						})}
					</Box>
				</Box>
			) : null}
		</Box>
	);
}
